import { renderPageShell } from '../lib/page-shell';
import { authorizedFetch, fetchMe, getToken } from '../lib/session';
import { fetchCatalog, fetchInventory, resolvePlayerTag, playerTagHtml, type CatalogItem } from '../lib/catalog';
import { escapeHtml as esc } from '../lib/format';
import type { Navigate, RouteHandler } from '../lib/router';

interface Player {
  id: string;
  username: string;
}

interface Friend extends Player {
  presence: 'online' | 'offline' | null;
}

type ChallengeGame = 'duel' | 'stack-tower';

interface SocialBoard {
  friends: Friend[];
  incoming: { id: string; user: Player }[];
  outgoing: { id: string; user: Player }[];
  blocked: Player[];
  incomingChallenges: { id: string; from: Player; game: ChallengeGame }[];
  outgoingChallenges: { id: string; to: Player; code: string; game: ChallengeGame }[];
  now: number;
}

function gameLabel(game: ChallengeGame): string {
  return game === 'stack-tower' ? 'Stack Tower' : 'a match';
}

/** A live-region-friendly status line above the board. */
function setStatus(el: HTMLElement | null, message: string, ok = true): void {
  if (!el) return;
  el.textContent = message;
  el.dataset.tone = ok ? 'ok' : 'error';
}

function presenceDot(presence: Friend['presence']): string {
  const online = presence === 'online';
  const label = online ? 'Online' : 'Offline';
  return `<span class="presence-dot${online ? ' presence-dot--online' : ''}" role="img" aria-label="${label}" title="${label}"></span>`;
}

/**
 * Removing a friend used to be a single always-visible tap — too easy to hit
 * by accident (owner brief). It now lives behind "More", with a confirm
 * step before it actually fires. Challenge/Stack Tower/Block stay direct
 * one-tap actions — the brief only asks to de-emphasise Remove specifically.
 */
function friendRow(f: Friend): string {
  return `
    <li class="friend">
      <span class="friend__who">${presenceDot(f.presence)}<strong>${esc(f.username)}</strong></span>
      <span class="friend__actions">
        <button class="button button--primary" data-action="challenge" data-user="${f.id}">Challenge</button>
        <button class="button button--primary" data-action="challenge-stack-tower" data-user="${f.id}">Stack Tower</button>
        <button class="button button--secondary" data-action="block" data-user="${f.id}">Block</button>
        <span class="friend-more">
          <button class="button button--secondary" type="button" data-action="more-toggle" aria-haspopup="true" aria-expanded="false">More&nbsp;▾</button>
          <div class="friend-more__menu" role="menu" hidden>
            <button type="button" class="friend-more__item" role="menuitem" data-action="remove" data-user="${f.id}" data-username="${esc(f.username)}">Remove friend</button>
          </div>
        </span>
      </span>
    </li>`;
}

function requestRow(r: { id: string; user: Player }, incoming: boolean): string {
  const actions = incoming
    ? `<button class="button button--primary" data-action="accept" data-id="${r.id}">Accept</button>
       <button class="button button--secondary" data-action="decline" data-id="${r.id}">Decline</button>`
    : `<button class="button button--secondary" data-action="cancel-request" data-user="${r.user.id}">Cancel</button>`;
  return `
    <li class="friend">
      <span class="friend__who"><strong>${esc(r.user.username)}</strong></span>
      <span class="friend__actions">${actions}</span>
    </li>`;
}

function incomingChallengeRow(c: { id: string; from: Player; game: ChallengeGame }): string {
  return `
    <li class="friend">
      <span class="friend__who"><span class="friend__id"><strong>${esc(c.from.username)}</strong><span class="friend__note">wants ${esc(gameLabel(c.game))}</span></span></span>
      <span class="friend__actions">
        <button class="button button--primary" data-action="accept-challenge" data-id="${c.id}">Accept</button>
        <button class="button button--secondary" data-action="decline-challenge" data-id="${c.id}">Decline</button>
      </span>
    </li>`;
}

function outgoingChallengeRow(c: { id: string; to: Player; code: string; game: ChallengeGame }): string {
  return `
    <li class="friend">
      <span class="friend__who"><span class="friend__id"><strong>${esc(c.to.username)}</strong><span class="friend__note">${esc(gameLabel(c.game))} · room ${esc(c.code)}</span></span></span>
      <span class="friend__actions">
        <button class="button button--primary" data-action="open-room" data-code="${esc(c.code)}" data-game="${c.game}">Open room</button>
        <button class="button button--secondary" data-action="cancel-challenge" data-id="${c.id}">Cancel</button>
      </span>
    </li>`;
}

function blockedRow(p: Player): string {
  return `
    <li class="friend">
      <span class="friend__who"><strong>${esc(p.username)}</strong></span>
      <span class="friend__actions">
        <button class="button button--secondary" data-action="unblock" data-user="${p.id}">Unblock</button>
      </span>
    </li>`;
}

function section(title: string, rows: string[]): string {
  if (rows.length === 0) return '';
  return `<h2 class="history-heading">${title}</h2><ul class="friend-list">${rows.join('')}</ul>`;
}

function renderBoard(board: SocialBoard): string {
  const empty =
    board.friends.length === 0 &&
    board.incoming.length === 0 &&
    board.outgoing.length === 0 &&
    board.incomingChallenges.length === 0 &&
    board.outgoingChallenges.length === 0 &&
    board.blocked.length === 0;
  if (empty) {
    return `<p class="settings-hint">No friends yet. Add someone by username above. Presence and direct challenges are only ever shared with accepted friends.</p>`;
  }
  return [
    section('Match invites', board.incomingChallenges.map(incomingChallengeRow)),
    section('Friend requests', board.incoming.map((r) => requestRow(r, true))),
    section('Friends', board.friends.map(friendRow)),
    section('Sent challenges', board.outgoingChallenges.map(outgoingChallengeRow)),
    section('Sent requests', board.outgoing.map((r) => requestRow(r, false))),
    section('Blocked', board.blocked.map(blockedRow)),
  ].join('');
}

export const renderFriends: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'FRIENDS',
    subtitle: 'Add friends, see who’s online, and challenge them to a custom match. Blocking is the master control.',
    body: `
      <div id="friend-my-tag"></div>
      <div class="friend-add">
        <input class="join-input" id="friend-username" maxlength="20" placeholder="Username" aria-label="Username to add as a friend" autocomplete="off" />
        <button class="button button--primary" type="button" data-action="add">Add friend</button>
      </div>
      <p class="form-error" id="friend-status" aria-live="polite"></p>
      <div id="friends-mount"><p class="attempt-list__empty">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#friends-mount')!;
  const status = body.querySelector<HTMLParagraphElement>('#friend-status');
  const myTagMount = body.querySelector<HTMLDivElement>('#friend-my-tag')!;

  if (!getToken()) {
    mount.innerHTML = `<p class="settings-hint">Sign in on the Account page to add friends.</p>`;
    return;
  }

  // The player's own equipped call sign/banner — previously never rendered
  // on this page at all (owner bug report, 2026-08-18: "should not
  // disappear simply because they are viewing the friends interface").
  // Best-effort: a catalog/inventory hiccup just leaves this mount empty
  // rather than blocking the friends board below it.
  void (async () => {
    const me = await fetchMe();
    if (!me) return;
    const [catalog, inventory] = await Promise.all([fetchCatalog(), fetchInventory()]);
    const byId = new Map<string, CatalogItem>(catalog.items.map((item) => [item.id, item]));
    const tag = resolvePlayerTag(byId, inventory.equipped);
    myTagMount.innerHTML = playerTagHtml(me.user.username, tag, esc);
  })().catch(() => {
    /* nice-to-have; the friends board itself doesn't depend on this */
  });

  let heartbeat: ReturnType<typeof setInterval> | null = null;

  async function reload(): Promise<void> {
    try {
      const board = await authorizedFetch<SocialBoard>('/api/social');
      mount.innerHTML = renderBoard(board);
    } catch {
      mount.innerHTML = `<p class="attempt-list__empty">Could not load your friends.</p>`;
    }
  }

  async function pingPresence(): Promise<void> {
    try {
      await authorizedFetch('/api/social/presence', { method: 'POST' });
    } catch {
      /* presence is best-effort; a missed heartbeat just reads as offline */
    }
  }

  async function post(path: string, bodyData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return authorizedFetch<Record<string, unknown>>(path, { method: 'POST', body: JSON.stringify(bodyData) });
  }

  function openRoom(nav: Navigate, code: string, game: ChallengeGame): void {
    const route = game === 'stack-tower' ? 'stack-tower-online' : 'play/online';
    nav(`${route}?code=${encodeURIComponent(code)}`);
  }

  async function addFriend(): Promise<void> {
    const input = body.querySelector<HTMLInputElement>('#friend-username');
    const username = input?.value.trim();
    if (!username) return;
    try {
      await post('/api/social/request', { username });
      if (input) input.value = '';
      setStatus(status, `Request sent to ${username}.`);
      await reload();
    } catch (error) {
      setStatus(status, error instanceof Error ? error.message : 'Could not send request.', false);
    }
  }

  /** At most one friend's "More" menu open at a time; any other click in the page closes it. */
  function closeAllMoreMenus(): void {
    body.querySelectorAll<HTMLElement>('.friend-more__menu').forEach((menu) => {
      menu.hidden = true;
    });
    body.querySelectorAll<HTMLButtonElement>('[data-action="more-toggle"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  // Closing an open "More" menu on outside click needs to cover the WHOLE
  // page, not just `body` (`#page-body`) — the page heading and header nav
  // are siblings of it, not descendants, so a body-scoped listener alone
  // never sees clicks there. Attached to `document`, cleaned up in this
  // view's own teardown (below) so it doesn't accumulate across repeat
  // visits to this page.
  const closeMenusOnOutsideClick = (event: MouseEvent): void => {
    if ((event.target as HTMLElement).closest('.friend-more')) return;
    closeAllMoreMenus();
  };
  document.addEventListener('click', closeMenusOnOutsideClick);

  // One delegated handler for the whole page body (add-form + board). `body` is
  // rebuilt by renderPageShell on every navigation, so this listener is dropped
  // with it — no accumulation across repeat visits.
  body.addEventListener('click', async (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-action]');

    if (target?.dataset.action === 'more-toggle') {
      const menu = target.closest<HTMLElement>('.friend-more')!.querySelector<HTMLElement>('.friend-more__menu')!;
      const wasOpen = !menu.hidden;
      closeAllMoreMenus();
      if (!wasOpen) {
        menu.hidden = false;
        target.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (!target) return;
    const action = target.dataset.action;
    const id = target.dataset.id;
    const userId = target.dataset.user;

    try {
      switch (action) {
        case 'add':
          await addFriend();
          return;
        case 'accept':
          await post('/api/social/respond', { id, action: 'accept' });
          break;
        case 'decline':
          await post('/api/social/respond', { id, action: 'decline' });
          break;
        case 'cancel-request':
          await post('/api/social/remove', { userId });
          break;
        case 'remove': {
          const name = target.dataset.username ?? 'this friend';
          if (!window.confirm(`Remove ${name} as a friend?`)) return;
          await post('/api/social/remove', { userId });
          break;
        }
        case 'block':
          await post('/api/social/block', { userId });
          break;
        case 'unblock':
          await post('/api/social/unblock', { userId });
          break;
        case 'challenge': {
          const res = await post('/api/social/challenge', { userId });
          if (typeof res.code === 'string') {
            openRoom(navigate, res.code, 'duel');
            return;
          }
          break;
        }
        case 'challenge-stack-tower': {
          const res = await post('/api/social/challenge', { userId, game: 'stack-tower' });
          if (typeof res.code === 'string') {
            openRoom(navigate, res.code, 'stack-tower');
            return;
          }
          break;
        }
        case 'accept-challenge': {
          const res = await post('/api/social/challenge/respond', { id, action: 'accept' });
          if (typeof res.code === 'string') {
            openRoom(navigate, res.code, res.game === 'stack-tower' ? 'stack-tower' : 'duel');
            return;
          }
          break;
        }
        case 'decline-challenge':
          await post('/api/social/challenge/respond', { id, action: 'decline' });
          break;
        case 'cancel-challenge':
          await post('/api/social/challenge/respond', { id, action: 'cancel' });
          break;
        case 'open-room':
          if (target.dataset.code) openRoom(navigate, target.dataset.code, target.dataset.game === 'stack-tower' ? 'stack-tower' : 'duel');
          return;
        default:
          return;
      }
      await reload();
    } catch (error) {
      setStatus(status, error instanceof Error ? error.message : 'Action failed.', false);
    }
  });

  // Announce presence immediately, then keep it fresh while this view is open.
  void pingPresence();
  heartbeat = setInterval(pingPresence, 60_000);
  void reload();

  return () => {
    if (heartbeat) clearInterval(heartbeat);
    document.removeEventListener('click', closeMenusOnOutsideClick);
  };
};
